import { _isNotBlank, _notNil } from '@/littledash';
import { formatBytes } from '@/helpers';

/** Splits a comma-separated `accept` string into its extension tokens (e.g. `.docx`). */
export const acceptedExtensions = (accept: string): Array<string> =>
  accept
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token.startsWith('.'));

/** True when the filename ends in one of the accept string's extensions (or none are configured). */
export const hasAcceptedExtension = (fileName: string, accept: string): boolean => {
  const extensions = acceptedExtensions(accept);
  if (extensions.length === 0) {
    return true;
  }
  const lower = fileName.toLowerCase();
  return extensions.some((ext) => lower.endsWith(ext.toLowerCase()));
};

/**
 * Validates a picked file against the field's accept/size rules, returning a
 * user-facing message when it fails and `null` when it passes.
 */
export const validateFile = (file: File, accept: string, maxSizeBytes?: number): string | null => {
  if (!hasAcceptedExtension(file.name, accept)) {
    const allowed = acceptedExtensions(accept).join(', ');
    const rejected = /\.([^.]+)$/.exec(file.name)?.[1]?.toUpperCase();
    const rejectedLabel = _notNil(rejected) ? `${rejected} isn’t supported` : 'That file type isn’t supported';
    return _isNotBlank(allowed) ? `${rejectedLabel} — upload ${allowed}` : `${rejectedLabel}.`;
  }
  if (_notNil(maxSizeBytes) && file.size > maxSizeBytes) {
    return `File is too large. Maximum size is ${formatBytes(maxSizeBytes)}.`;
  }
  return null;
};

/**
 * Derives a human-friendly filename from a stored upload url. The step this
 * field lives in unmounts when collapsed, so local file state is lost on
 * reopen; the RHF value (the url) survives, and this recovers a display name
 * from it. Falls back to a generic label when the url cannot be parsed.
 */
export const fileNameFromUrl = (url: string): string => {
  try {
    const lastSegment = new URL(url).pathname.split('/').pop() ?? '';
    const decoded = decodeURIComponent(lastSegment);
    return _isNotBlank(decoded) ? decoded : 'Uploaded document';
  } catch {
    return 'Uploaded document';
  }
};
